import React, { useMemo, useState, useCallback } from 'react';
import { Box, Chip, CircularProgress, TextField, Autocomplete } from '@mui/material';

import { useTaggedItems } from '../hooks/useTaggedItems';
import { useTagSearch } from '../hooks/useTagSearch';
import { Tag } from '../types';

interface TagsProps {
  objectId: string;
  contentType: string;
  disabled?: boolean;
}

const PAGE_SIZE = 20;

const TaggedItemsEditor: React.FC<TagsProps> = ({ objectId, contentType, disabled = false }) => {
  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);

  React.useEffect(() => {
    const handler = setTimeout(() => setSearch(inputValue), 400);
    return () => clearTimeout(handler);
  }, [inputValue]);

  const { allOptions, isLoading, isFetching, data, clearAll } = useTagSearch(open, search, page, PAGE_SIZE);

  const {
    taggedItems,
    isLoading: isTaggedItemsLoading,
    addTag,
    removeTag
  } = useTaggedItems({
    objectId: objectId,
    contentType: contentType
  });

  const tagOptions = useMemo(
    () => allOptions.filter(tag => !taggedItems.some(item => item.tag_id === tag.id)),
    [allOptions, taggedItems]
  );

  const handleLoadMoreTagsOnScroll = useCallback(
    (event: React.UIEvent<HTMLUListElement>) => {
      const listboxNode = event.currentTarget;
      if (
        listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 1 &&
        !isFetching &&
        data?.data.length === PAGE_SIZE
      ) {
        setPage(prev => prev + 1);
      }
    },
    [isFetching, data]
  );

  React.useEffect(() => {
    setPage(1);
  }, [search]);

  const handleTagAdd = async (_event: unknown, newTag: Tag | null) => {
    if (newTag) {
      await addTag(newTag.id);
      setInputValue('');
      clearAll();
    }
  };

  const handleTagDelete = async (taggedItemId: string) => {
    await removeTag(taggedItemId);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {isTaggedItemsLoading ? (
          <CircularProgress size={18} />
        ) : (
          taggedItems.map(item => (
            <Chip
              key={item.id}
              label={`${item.tag_set_name}: ${item.tag_display_name}`}
              onDelete={disabled ? undefined : () => handleTagDelete(item.id)}
              size="small"
              disabled={disabled}
            />
          ))
        )}
      </Box>
      <Autocomplete
        disablePortal
        options={tagOptions}
        getOptionLabel={option => `${option.tag_set_name}: ${option.display_name}`}
        value={null}
        inputValue={inputValue}
        onInputChange={(_event, newValue) => setInputValue(newValue)}
        onChange={handleTagAdd}
        renderInput={params => (
          <TextField
            {...params}
            label="Add tag"
            disabled={disabled}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {(isLoading || isFetching) && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        loading={isLoading || isFetching}
        disabled={disabled}
        filterOptions={x => x}
        noOptionsText={search ? 'No tags found' : 'Type to search tags'}
        ListboxProps={{
          onScroll: handleLoadMoreTagsOnScroll
        }}
      />
    </Box>
  );
};

export default TaggedItemsEditor;
